import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LocalizedName, localizedName } from '@/components/localized-name';
import { cn } from '@/lib/utils';
import { benefitTypeLabelKey } from './membership-enums';
import { EditBenefitSheet } from './edit-benefit-sheet';
import { useMembershipBenefits } from './memberships.api';
import type { MembershipBenefit, MembershipPlan } from './types';

/** A yes/no column, as the original's green tick and red cross. */
function BoolCell({ value }: { value: boolean }) {
  return value ? (
    <Check className="text-success mx-auto size-4" />
  ) : (
    <X className="text-destructive mx-auto size-4" />
  );
}

/**
 * The benefits attached to one plan.
 *
 * Table on a wide screen, cards below `md` — the original's `ResponsiveTable`
 * made the same switch. The "add benefit" button is in the header rather than
 * floated above the list: in the original it was `float-right`, and because
 * floats paint beneath positioned elements the first benefit card painted over
 * it on a phone and swallowed every tap.
 */
export function PlanBenefitsSheet({
  plan,
  open,
  onOpenChange,
}: {
  plan: MembershipPlan | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState<MembershipBenefit | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: benefits, isLoading, isFetching, isError } =
    useMembershipBenefits(open ? (plan?.id ?? null) : null);

  if (!plan) return null;

  const rows = benefits ?? [];

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
        >
          {/* pr-12 clears the sheet's own close button, which is pinned at
              `top-4 right-4` — without it the action sits underneath the X. */}
          <SheetHeader className="flex-row flex-wrap items-start justify-between gap-3 border-b pr-12">
            <SheetTitle>
              {localizedName(plan, i18n.resolvedLanguage ?? 'en')}
              <span className="text-muted-foreground ml-1.5 font-normal">
                · {t('memberships.benefits')}
              </span>
            </SheetTitle>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus />
              {t('memberships.addBenefit')}
            </Button>
          </SheetHeader>

          <div className="space-y-4 p-4">
            {plan.imageUrl && (
              <img
                src={plan.imageUrl}
                alt=""
                className="bg-muted mx-auto max-h-48 w-full max-w-sm rounded-lg object-contain"
              />
            )}

            {isLoading && (
              <div className="divide-y rounded-lg border">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-3 p-3">
                    <Skeleton className="h-4 flex-1" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                ))}
              </div>
            )}

            {isError && (
              <p className="text-muted-foreground py-10 text-center text-sm">
                {t('memberships.benefitsFailed')}
              </p>
            )}

            {benefits && rows.length === 0 && (
              <div className="relative rounded-lg">
                <BusyOverlay show={isFetching} />
                <p className="text-muted-foreground rounded-lg border border-dashed py-10 text-center text-sm">
                  {t('memberships.noBenefits')}
                </p>
              </div>
            )}

            {rows.length > 0 && (
              <>
                <div className="hidden rounded-lg border md:block">
                  <Table busy={isFetching}>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>{t('memberships.name')}</TableHead>
                        <TableHead className="text-center">
                          {t('memberships.isActive')}
                        </TableHead>
                        <TableHead className="text-center">
                          {t('memberships.type')}
                        </TableHead>
                        <TableHead className="text-center">
                          {t('memberships.isPromoBenefit')}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('memberships.actions')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <LocalizedName
                              nameEn={row.nameEn}
                              nameEs={row.nameEs}
                              internalName={row.internalName}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <BoolCell value={row.isActive} />
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            {t(benefitTypeLabelKey(row.type))}
                          </TableCell>
                          <TableCell className="text-center">
                            <BoolCell value={row.isPromoBenefit} />
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" onClick={() => setEditing(row)}>
                              {t('memberships.edit')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="relative md:hidden">
                  <BusyOverlay show={isFetching} />
                  <div
                    className={cn(
                      'space-y-2',
                      isFetching && 'opacity-60 transition-opacity'
                    )}
                  >
                    {rows.map((row) => (
                      <div
                        key={row.id}
                        className="bg-card space-y-2 rounded-xl border p-3"
                      >
                        <LocalizedName
                          nameEn={row.nameEn}
                          nameEs={row.nameEs}
                          internalName={row.internalName}
                        />
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant={row.isActive ? 'success' : 'secondary'}>
                            {row.isActive
                              ? t('memberships.active')
                              : t('memberships.inactive')}
                          </Badge>
                          {row.isPromoBenefit && (
                            <Badge variant="warning">
                              {t('memberships.isPromoBenefit')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground text-xs">
                          {t('memberships.type')}:{' '}
                          {t(benefitTypeLabelKey(row.type))}
                        </p>
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => setEditing(row)}
                        >
                          {t('memberships.edit')}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <EditBenefitSheet
        benefit={editing}
        planId={plan.id}
        open={editing !== null || creating}
        onOpenChange={(next) => {
          if (!next) {
            setEditing(null);
            setCreating(false);
          }
        }}
      />
    </>
  );
}
